import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, CloudArrowUp, X, Car, CircleNotch } from '@phosphor-icons/react';
import { toast } from 'sonner';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProReception = () => {
  const [images, setImages] = useState([]);
  const [form, setForm] = useState({ vin: '', registration: '', brand: '', model: '', year: '', mileage: '', color: '', fuel_type: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState('');
  const fileInputRef = useRef(null);
  const cameraRef = useRef(null);
  const navigate = useNavigate();

  const handleFiles = (files) => {
    const newImages = [];
    Array.from(files).forEach(file => {
      if (images.length + newImages.length >= 10) return;
      const reader = new FileReader();
      reader.onload = () => {
        setImages(prev => [...prev, { file, preview: reader.result }]);
      };
      reader.readAsDataURL(file);
      newImages.push(file);
    });
  };

  const removeImage = (idx) => setImages(prev => prev.filter((_, i) => i !== idx));

  const handleSubmit = async () => {
    if (images.length === 0) { toast.error('Ajoutez au moins une photo du véhicule'); return; }
    setSubmitting(true);

    try {
      // 1. Create vehicle
      setStep('Création du véhicule...');
      const createResp = await axios.post(`${API_URL}/api/pro/vehicles`);
      const vehicleId = createResp.data.id;

      // 2. Update vehicle info
      setStep('Enregistrement des informations...');
      const info = {};
      Object.entries(form).forEach(([k, v]) => { if (v) info[k] = k === 'mileage' ? parseInt(v) : v; });
      if (Object.keys(info).length > 0) {
        await axios.put(`${API_URL}/api/pro/vehicles/${vehicleId}`, info);
      }

      // 3. Upload photos
      setStep('Upload des photos...');
      await axios.post(`${API_URL}/api/pro/vehicles/${vehicleId}/photos`, {
        images: images.map(img => img.preview)
      });

      // 4. Launch analysis
      setStep('Lancement de l\'analyse IA...');
      await axios.post(`${API_URL}/api/pro/vehicles/${vehicleId}/analyze`);

      toast.success('Véhicule réceptionné ! L\'analyse est en cours...');
      navigate(`/admin/pro/vehicle/${vehicleId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la réception');
      setSubmitting(false);
      setStep('');
    }
  };

  return (
    <AdminLayout title="Réception véhicule">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white border border-gray-200 shadow-sm p-8 mb-6">
          <h2 className="font-['Outfit'] text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
            <Camera size={20} className="text-[#E63946]" />
            Photos du véhicule ({images.length}/10)
          </h2>

          <div
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-gray-300 p-8 text-center cursor-pointer hover:border-[#E63946] transition-colors mb-4"
            data-testid="photo-dropzone"
          >
            <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={e => handleFiles(e.target.files)} className="hidden" data-testid="photo-input" />
            <CloudArrowUp size={48} className="text-gray-300 mx-auto mb-3" />
            <p className="text-gray-700 font-medium">Cliquez pour ajouter des photos</p>
            <p className="text-gray-400 text-sm mt-1">Prenez le véhicule sous tous les angles (avant, arrière, côtés, moteur)</p>
          </div>

          <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={e => { if (e.target.files[0]) handleFiles(e.target.files); e.target.value = ''; }} className="hidden" />
          <button type="button" onClick={() => cameraRef.current?.click()} className="w-full flex items-center justify-center gap-3 py-4 border-2 border-[#E63946] text-[#E63946] font-semibold text-sm mb-4" data-testid="camera-btn">
            <Camera size={22} weight="bold" /> Prendre une photo
          </button>

          {images.length > 0 && (
            <div className="grid grid-cols-5 gap-3">
              {images.map((img, i) => (
                <div key={i} className="relative aspect-square bg-gray-100 overflow-hidden group">
                  <img src={img.preview} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => removeImage(i)} className="absolute top-1 right-1 w-6 h-6 bg-[#E63946] text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-photo-${i}`}>
                    <X size={14} weight="bold" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white border border-gray-200 shadow-sm p-8 mb-6">
          <h2 className="font-['Outfit'] text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
            <Car size={20} className="text-[#E63946]" />
            Informations véhicule
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-600 text-sm mb-1">Plaque d'immatriculation</label>
              <input type="text" value={form.registration} onChange={e => setForm(f => ({ ...f, registration: e.target.value }))} placeholder="AB-123-CD" className="w-full border border-gray-300 p-3 text-sm font-mono uppercase focus:border-[#E63946] outline-none" data-testid="input-reg" />
            </div>
            <div>
              <label className="block text-gray-600 text-sm mb-1">VIN</label>
              <input type="text" value={form.vin} onChange={e => setForm(f => ({ ...f, vin: e.target.value }))} placeholder="VF1RFB00X56789012" maxLength={17} className="w-full border border-gray-300 p-3 text-sm font-mono uppercase focus:border-[#E63946] outline-none" data-testid="input-vin" />
            </div>
            <div>
              <label className="block text-gray-600 text-sm mb-1">Marque</label>
              <input type="text" value={form.brand} onChange={e => setForm(f => ({ ...f, brand: e.target.value }))} placeholder="Peugeot, Renault, Citroen..." className="w-full border border-gray-300 p-3 text-sm focus:border-[#E63946] outline-none" data-testid="input-brand" />
            </div>
            <div>
              <label className="block text-gray-600 text-sm mb-1">Modèle</label>
              <input type="text" value={form.model} onChange={e => setForm(f => ({ ...f, model: e.target.value }))} placeholder="308, Clio, Berlingo..." className="w-full border border-gray-300 p-3 text-sm focus:border-[#E63946] outline-none" data-testid="input-model" />
            </div>
            <div>
              <label className="block text-gray-600 text-sm mb-1">Année</label>
              <input type="text" value={form.year} onChange={e => setForm(f => ({ ...f, year: e.target.value }))} placeholder="2020" className="w-full border border-gray-300 p-3 text-sm focus:border-[#E63946] outline-none" data-testid="input-year" />
            </div>
            <div>
              <label className="block text-gray-600 text-sm mb-1">Kilométrage</label>
              <input type="number" value={form.mileage} onChange={e => setForm(f => ({ ...f, mileage: e.target.value }))} placeholder="85000" className="w-full border border-gray-300 p-3 text-sm focus:border-[#E63946] outline-none" data-testid="input-mileage" />
            </div>
            <div>
              <label className="block text-gray-600 text-sm mb-1">Couleur</label>
              <input type="text" value={form.color} onChange={e => setForm(f => ({ ...f, color: e.target.value }))} placeholder="Gris, Blanc, Noir..." className="w-full border border-gray-300 p-3 text-sm focus:border-[#E63946] outline-none" data-testid="input-color" />
            </div>
            <div>
              <label className="block text-gray-600 text-sm mb-1">Carburant</label>
              <select value={form.fuel_type} onChange={e => setForm(f => ({ ...f, fuel_type: e.target.value }))} className="w-full border border-gray-300 p-3 text-sm focus:border-[#E63946] outline-none bg-white" data-testid="input-fuel">
                <option value="">Sélectionner</option>
                <option value="essence">Essence</option>
                <option value="diesel">Diesel</option>
                <option value="hybride">Hybride</option>
                <option value="electrique">Électrique</option>
                <option value="gpl">GPL</option>
              </select>
            </div>
          </div>
          <div className="mt-4">
            <label className="block text-gray-600 text-sm mb-1">Notes</label>
            <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={3} placeholder="État général, dommages visibles, historique..." className="w-full border border-gray-300 p-3 text-sm focus:border-[#E63946] outline-none resize-none" data-testid="input-notes" />
          </div>
        </div>

        <button
          onClick={handleSubmit}
          disabled={submitting || images.length === 0}
          className={`w-full py-5 text-lg font-bold uppercase tracking-wide transition-colors ${submitting ? 'bg-gray-400 cursor-wait' : images.length === 0 ? 'bg-gray-300 cursor-not-allowed' : 'bg-[#E63946] hover:bg-[#D90429] text-white'}`}
          data-testid="submit-vehicle"
        >
          {submitting ? (
            <span className="flex items-center justify-center gap-3">
              <CircleNotch size={24} className="animate-spin" />
              {step}
            </span>
          ) : 'Réceptionner et analyser le véhicule'}
        </button>
      </div>
    </AdminLayout>
  );
};

export default ProReception;
